import gql from 'graphql-tag';
import {useMutation, useQuery} from '@apollo/client';
import {RequestFPA, RequestFPAVariables} from '../../schema-types/RequestFPA';
import {GetLatestFPA, GetLatestFPAVariables} from '../../schema-types/GetLatestFPA';
import {GetDocumentSignaturesTemplates} from '../../schema-types/GetDocumentSignaturesTemplates';
import LIST_CONTACTS from '../AccountContactsGrid/accountContactQueries';
import {CONTACT_STATUSES} from '../../pages/PrecursorSummaryPage/useContactStatuses';
import validateUUID from '../../utils/validateUUID';
import {ResendFPA, ResendFPAVariables} from '../../schema-types/ResendFPA';

export const GET_FPA_DOCUMENTS = gql`
  query GetLatestFPA($accountId: UUID!, $contactId: UUID!) {
    latestFpa(accountId: $accountId, contactId: $contactId) {
      id
      scope
      status
      dateCreated
      signed
      dateLastSent
      dateSigned
    }
  }
`;

export const REQUEST_FPA_SIGNATURE = gql`
  mutation RequestFPA(
    $accountId: UUID!
    $contactId: UUID!
    $scope: String!
    $documentName: String
    $documentFileName: String
  ) {
    requestFpa(
      accountId: $accountId
      contactId: $contactId
      scope: $scope
      documentName: $documentName
      documentFileName: $documentFileName
    ) {
      ok
      fpa {
        id
        scope
        status
        signed
        dateCreated
        dateLastSent
        dateSigned
      }
      errors
    }
  }
`;

export const RESEND_FPA_SIGNATURE = gql`
  mutation ResendFPA($accountId: UUID!, $contactId: UUID!, $documentName: String) {
    resendFpa(accountId: $accountId, contactId: $contactId, documentName: $documentName) {
      ok
      fpa {
        id
        scope
        status
        signed
        dateCreated
        dateLastSent
        dateSigned
      }
      errors
    }
  }
`;

export const DOCUMENT_SIGNATURE_TEMPLATE = gql`
  query GetDocumentSignaturesTemplates {
    documentSignaturesTemplates {
      fileName
      type
      createdDate
    }
  }
`;

export const useFPAdocuments = ({
  accountId,
  contactId,
}: {
  accountId: string | null;
  contactId: string | null;
}) => {
  const [
    getSignature,
    {data: mutationData, loading: mutationLoading, error: mutationError},
  ] = useMutation<RequestFPA, RequestFPAVariables>(REQUEST_FPA_SIGNATURE, {
    refetchQueries: [GET_FPA_DOCUMENTS, LIST_CONTACTS, CONTACT_STATUSES],
    awaitRefetchQueries: true,
  });

  const [
    resendSignature,
    {data: resendData, loading: resendLoading, error: resendError},
  ] = useMutation<ResendFPA, ResendFPAVariables>(RESEND_FPA_SIGNATURE, {
    refetchQueries: [GET_FPA_DOCUMENTS, LIST_CONTACTS, CONTACT_STATUSES],
    awaitRefetchQueries: true,
  });

  const {data, loading, error} = useQuery<GetLatestFPA, GetLatestFPAVariables>(
    GET_FPA_DOCUMENTS,
    {
      variables: {
        accountId: validateUUID(accountId)!,
        contactId: validateUUID(contactId)!,
      },
      skip: !validateUUID(accountId) || !validateUUID(contactId),
    }
  );

  const {
    data: documentSignaturesTemplatesData,
    loading: documentSignaturesTemplatesLoading,
    error: documentSignaturesTemplatesError,
  } = useQuery<GetDocumentSignaturesTemplates>(DOCUMENT_SIGNATURE_TEMPLATE);

  return {
    getSignature,
    fpaLoading: loading,
    fpaError: error,
    latestFpa: data?.latestFpa,
    mutationLoading,
    mutationError,
    documentSignaturesTemplates:
      documentSignaturesTemplatesData?.documentSignaturesTemplates || [],
    documentSignaturesTemplatesLoading,
    documentSignaturesTemplatesError,
    resendData,
    resendError,
    resendLoading,
    resendSignature,
  };
};
